import { useEffect, useMemo, useState } from "react";
import { Download, Search, X, Trash2, FileText, ChevronRight, Filter, Flame } from "lucide-react";
import { toast } from "sonner";
import api, { API_BASE, formatApiError } from "@/lib/api";

const STATUSES = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "proposal_sent", label: "Proposal Sent" },
  { value: "converted", label: "Converted" },
  { value: "dropped", label: "Dropped" },
];

const SCORE_LABELS = [
  { value: "hot", label: "Hot" },
  { value: "warm", label: "Warm" },
  { value: "cold", label: "Cold" },
];

const VENUE_STATUS_OPTIONS = ["Yes", "No", "Shortlisting Options", "Need Help Finding One"];

const OVERALL_BUDGET_OPTIONS = [
  "Under \u20B910 Lakhs",
  "\u20B910\u201320 Lakhs",
  "\u20B920\u201335 Lakhs",
  "\u20B935\u201350 Lakhs",
  "\u20B950\u201375 Lakhs",
  "\u20B975 Lakhs\u20131 Crore",
  "\u20B91 Crore+",
];

const PLANNING_BUDGET_OPTIONS = [
  "Under \u20B91 Lakh",
  "\u20B91\u20132 Lakhs",
  "\u20B92\u20134 Lakhs",
  "\u20B94\u20136 Lakhs",
  "\u20B96\u201310 Lakhs",
  "\u20B910 Lakhs+",
];

const EMPTY_FILTERS = {
  status: "all",
  lead_score_label: "all",
  venue_status: "all",
  overall_budget: "all",
  planning_budget: "all",
  wedding_location: "",
};

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");

  const load = async () => {
    setLoading(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v && v !== "all") params[k] = v;
    });
    if (q) params.q = q;
    try {
      const res = await api.get("/leads", { params });
      setLeads(res.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const params = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v && v !== "all") params[k] = v;
    });
    if (q) params.q = q;
    queueMicrotask(() => { if (!cancelled) setLoading(true); });
    api.get("/leads", { params })
      .then((res) => {
        if (cancelled) return;
        queueMicrotask(() => setLeads(res.data || []));
      })
      .finally(() => {
        if (cancelled) return;
        queueMicrotask(() => setLoading(false));
      });
    return () => { cancelled = true; };
  }, [filters]);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/leads/${id}`, { status });
      setLeads((all) => all.map((l) => (l.id === id ? data : l)));
      if (selected?.id === id) setSelected(data);
      toast.success("Status updated");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const saveNotes = async () => {
    if (!selected) return;
    try {
      const { data } = await api.patch(`/leads/${selected.id}`, { notes });
      setLeads((all) => all.map((l) => (l.id === selected.id ? data : l)));
      setSelected(data);
      toast.success("Notes saved");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await api.delete(`/leads/${id}`);
      setLeads((all) => all.filter((l) => l.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Lead deleted");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const exportCsv = async () => {
    const token = window.localStorage.getItem("mostash_token");
    const res = await fetch(`${API_BASE}/leads/export.csv`, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
    if (!res.ok) { toast.error("Export failed"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "leads.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = useMemo(() => {
    if (!q) return leads;
    const ql = q.toLowerCase();
    return leads.filter((l) => [l.name, l.email, l.phone, l.wedding_location, l.message].some((v) => (v || "").toLowerCase().includes(ql)));
  }, [leads, q]);

  const setFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));
  const resetFilters = () => { setFilters(EMPTY_FILTERS); setQ(""); };
  const activeFilterCount = Object.entries(filters).filter(([, v]) => v && v !== "all").length + (q ? 1 : 0);

  return (
    <div data-testid="admin-leads-page">
      <header className="mb-6 md:mb-10 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="uppercase tracking-[0.3em] text-[10px] text-charcoal/50 mb-2">Pipeline</div>
          <h1 className="font-display text-3xl md:text-4xl text-charcoal">Leads</h1>
          <p className="text-charcoal/65 font-light mt-2">All inquiries received from the website.</p>
        </div>
        <button type="button" onClick={exportCsv} data-testid="leads-export-csv" className="btn-secondary self-start">
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </header>

      {/* Search + status quick filters */}
      <div className="bg-white border border-beige p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-3 md:gap-4 mb-3">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40" />
          <input
            data-testid="leads-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder={"Search by name, email, phone, location\u2026"}
            className="w-full h-11 pl-9 pr-3 bg-ivory border border-beige focus:border-teal-600 outline-none text-sm"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1">
          {[{ value: "all", label: "All" }, ...STATUSES].map((s) => (
            <button
              key={s.value}
              type="button"
              data-testid={`leads-filter-${s.value}`}
              onClick={() => setFilter("status", s.value)}
              className={`shrink-0 px-3 h-9 text-[11px] uppercase tracking-[0.2em] border ${filters.status === s.value ? "bg-teal-600 text-ivory border-teal-600" : "text-charcoal/70 border-beige hover:border-teal-600"}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Advanced filters */}
      <div className="bg-white border border-beige p-4 md:p-5 mb-5" data-testid="leads-advanced-filters">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-charcoal/65">
            <Filter className="h-3.5 w-3.5" />
            <span className="uppercase tracking-[0.22em] text-[10px] font-medium">Filter by</span>
            {activeFilterCount > 0 ? (
              <span className="ml-2 inline-flex items-center justify-center min-w-5 h-5 px-1.5 bg-teal-600 text-ivory text-[10px] font-medium">{activeFilterCount}</span>
            ) : null}
          </div>
          {activeFilterCount > 0 ? (
            <button type="button" onClick={resetFilters} data-testid="leads-clear-filters" className="text-[10px] uppercase tracking-[0.22em] text-charcoal/60 hover:text-teal-600">
              Clear all
            </button>
          ) : null}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <FilterSelect testid="filter-score" label="Lead score" value={filters.lead_score_label} onChange={(v) => setFilter("lead_score_label", v)} options={SCORE_LABELS} />
          <FilterSelect testid="filter-venue-status" label="Venue status" value={filters.venue_status} onChange={(v) => setFilter("venue_status", v)} options={VENUE_STATUS_OPTIONS.map((v) => ({ value: v, label: v }))} />
          <FilterSelect testid="filter-overall-budget" label="Overall budget" value={filters.overall_budget} onChange={(v) => setFilter("overall_budget", v)} options={OVERALL_BUDGET_OPTIONS.map((v) => ({ value: v, label: v }))} />
          <FilterSelect testid="filter-planning-budget" label="Planning budget" value={filters.planning_budget} onChange={(v) => setFilter("planning_budget", v)} options={PLANNING_BUDGET_OPTIONS.map((v) => ({ value: v, label: v }))} />
          <label className="block">
            <span className="uppercase tracking-[0.22em] text-[9px] text-charcoal/55">Wedding location</span>
            <input
              type="text"
              value={filters.wedding_location}
              onChange={(e) => setFilter("wedding_location", e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              data-testid="filter-wedding-location"
              placeholder="e.g. Goa"
              className="mt-1 w-full h-9 px-3 bg-ivory border border-beige focus:border-teal-600 outline-none text-sm"
            />
          </label>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-beige overflow-hidden" data-testid="leads-table">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1100px]">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.22em] text-charcoal/55 bg-sage/30">
                <th className="px-4 py-4">Score</th>
                <th className="px-4 py-4">Name</th>
                <th className="px-4 py-4">Wedding Location</th>
                <th className="px-4 py-4">Wedding Date</th>
                <th className="px-4 py-4">Venue Status</th>
                <th className="px-4 py-4">Guest Count</th>
                <th className="px-4 py-4">Overall Budget</th>
                <th className="px-4 py-4">Planning Budget</th>
                <th className="px-4 py-4">Status</th>
                <th className="px-4 py-4">Created</th>
                <th className="px-4 py-4 text-right">Open</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={11} className="px-5 py-10 text-charcoal/40 text-center">Loading&hellip;</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={11} className="px-5 py-10 text-charcoal/40 italic text-center">No leads match this filter.</td></tr>
              ) : (
                filtered.map((l) => (
                  <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-t border-beige hover:bg-sage/15 cursor-pointer" onClick={() => { setSelected(l); setNotes(l.notes || ""); }}>
                    <td className="px-4 py-4"><ScorePill label={l.lead_score_label} score={l.lead_score} /></td>
                    <td className="px-4 py-4">
                      <div className="font-display text-base text-charcoal whitespace-nowrap">{l.name}</div>
                      <div className="text-xs text-charcoal/55 whitespace-nowrap">{l.email}</div>
                    </td>
                    <td className="px-4 py-4 text-sm text-charcoal/75">{l.wedding_location || "\u2014"}</td>
                    <td className="px-4 py-4 text-sm text-charcoal/75">{l.wedding_date || "\u2014"}</td>
                    <td className="px-4 py-4 text-sm text-charcoal/75 whitespace-nowrap">{l.venue_status || "\u2014"}</td>
                    <td className="px-4 py-4 text-sm text-charcoal/75 whitespace-nowrap">{l.guest_count || "\u2014"}</td>
                    <td className="px-4 py-4 text-sm text-charcoal/75 whitespace-nowrap">{l.overall_budget || "\u2014"}</td>
                    <td className="px-4 py-4 text-sm text-charcoal/75 whitespace-nowrap">{l.planning_budget || "\u2014"}</td>
                    <td className="px-4 py-4"><StatusPill status={l.status} /></td>
                    <td className="px-4 py-4 text-xs text-charcoal/60 whitespace-nowrap">{fmtDate(l.created_at)}</td>
                    <td className="px-4 py-4 text-right"><ChevronRight className="h-4 w-4 text-charcoal/40 inline" /></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail drawer */}
      {selected ? (
        <div className="fixed inset-0 z-50" data-testid="lead-drawer">
          <div className="absolute inset-0 bg-charcoal/40" onClick={() => setSelected(null)} />
          <aside className="absolute top-0 right-0 h-full w-full max-w-xl bg-ivory border-l border-beige overflow-y-auto">
            <header className="sticky top-0 bg-ivory border-b border-beige px-6 py-5 flex items-center justify-between z-10">
              <div>
                <div className="uppercase tracking-[0.3em] text-[10px] text-charcoal/55">Lead</div>
                <div className="font-display text-2xl text-charcoal">{selected.name}</div>
                <div className="mt-2"><ScorePill label={selected.lead_score_label} score={selected.lead_score} /></div>
              </div>
              <button type="button" onClick={() => setSelected(null)} data-testid="lead-drawer-close" className="h-10 w-10 border border-beige inline-flex items-center justify-center hover:bg-charcoal hover:text-ivory transition-colors">
                <X className="h-4 w-4" />
              </button>
            </header>
            <div className="px-6 py-7 space-y-7">
              <DetailRow label="Email" value={<a href={`mailto:${selected.email}`} className="hover:text-teal-600">{selected.email}</a>} />
              <DetailRow label="Phone" value={<a href={`tel:${selected.phone}`} className="hover:text-teal-600">{selected.phone}</a>} />
              <DetailRow label="Wedding date" value={selected.wedding_date || "\u2014"} />
              <DetailRow label="Wedding location" value={selected.wedding_location || "\u2014"} />
              <DetailRow label="Venue status" value={selected.venue_status || "\u2014"} />
              <DetailRow label="Guest count" value={selected.guest_count || "\u2014"} />
              <DetailRow label="Overall budget" value={selected.overall_budget || "\u2014"} />
              <DetailRow label="Planning budget" value={selected.planning_budget || "\u2014"} />
              <DetailRow label="Lead source" value={selected.source || "website"} />
              <DetailRow label="Received" value={fmtDate(selected.created_at)} />
              <div>
                <div className="uppercase tracking-[0.25em] text-[10px] text-charcoal/55 mb-2">Vision</div>
                <p className="text-charcoal/85 font-light whitespace-pre-wrap leading-relaxed">{selected.message || "No vision provided."}</p>
              </div>

              <div>
                <div className="uppercase tracking-[0.25em] text-[10px] text-charcoal/55 mb-3">Update status</div>
                <div className="flex flex-wrap gap-2">
                  {STATUSES.map((s) => (
                    <button
                      key={s.value}
                      type="button"
                      data-testid={`lead-status-${s.value}`}
                      onClick={() => updateStatus(selected.id, s.value)}
                      className={`px-3 h-9 text-[11px] uppercase tracking-[0.2em] border ${selected.status === s.value ? "bg-teal-600 text-ivory border-teal-600" : "text-charcoal border-beige hover:border-teal-600"}`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="uppercase tracking-[0.25em] text-[10px] text-charcoal/55 mb-2 flex items-center gap-2">
                  <FileText className="h-3.5 w-3.5" /> Internal notes
                </div>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  data-testid="lead-notes"
                  rows={5}
                  className="w-full bg-white border border-beige p-3 text-sm font-light focus:border-teal-600 outline-none resize-none"
                  placeholder={"Private notes for the team\u2026"}
                />
                <button type="button" onClick={saveNotes} data-testid="lead-notes-save" className="mt-3 btn-primary text-[11px] h-10 px-5">Save notes</button>
              </div>

              <button type="button" onClick={() => remove(selected.id)} data-testid="lead-delete" className="inline-flex items-center gap-2 text-rose-700 text-[11px] uppercase tracking-[0.2em] hover:text-rose-900">
                <Trash2 className="h-3.5 w-3.5" /> Delete lead
              </button>
            </div>
          </aside>
        </div>
      ) : null}
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="uppercase tracking-[0.22em] text-[9px] text-charcoal/55">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-1 w-full h-9 px-2 bg-ivory border border-beige focus:border-teal-600 outline-none text-xs"
      >
        <option value="all">All</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-4 text-sm">
      <div className="uppercase tracking-[0.22em] text-[10px] text-charcoal/55 col-span-1 pt-0.5">{label}</div>
      <div className="col-span-2 text-charcoal/85 font-light">{value}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    new: "bg-teal-600/10 text-teal-700",
    contacted: "bg-gold/15 text-gold-600",
    proposal_sent: "bg-beige text-charcoal",
    converted: "bg-emerald-100 text-emerald-700",
    dropped: "bg-rose-100 text-rose-700",
  };
  return <span className={`inline-block px-3 py-1 text-[10px] uppercase tracking-[0.22em] font-medium whitespace-nowrap ${map[status] || map.new}`}>{(status || "new").replace("_", " ")}</span>;
}

function ScorePill({ label, score }) {
  const styles = {
    hot:  { bg: "bg-rose-600", fg: "text-ivory", text: "Hot" },
    warm: { bg: "bg-amber-500", fg: "text-ivory", text: "Warm" },
    cold: { bg: "bg-slate-500", fg: "text-ivory", text: "Cold" },
  };
  const s = styles[(label || "cold").toLowerCase()] || styles.cold;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-[0.22em] font-medium ${s.bg} ${s.fg}`} data-testid={`score-pill-${label}`}>
      {label === "hot" ? <Flame className="h-3 w-3" /> : null}
      {s.text}
      {typeof score === "number" ? <span className="opacity-70 ml-1">{score}</span> : null}
    </span>
  );
}

function fmtDate(d) {
  if (!d) return "\u2014";
  try {
    const date = typeof d === "string" ? new Date(d) : d;
    return date.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) + " \u00B7 " +
      date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  } catch {
    return String(d);
  }
}
